import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import FormSelection from "../components/Form/FormSelection";
import { enumToArraySelection, onHandleFormSubmit } from "../lib/helper";
import {
  ChartDataSourceEnum,
  BarDirectionEnum,
  ChartTickesFormatEnum,
  Chart,
  ChartTypeEnum,
  ChartGroupByEnum,
  TableDateRangeEnum,
  TableDataFormationEnum,
  CHART_DECIMAL_PLACES_OPTIONS,
} from "../models/company.model";
import { Box, Button, Grid, IconButton, InputAdornment, Paper, Stack, Typography } from "@mui/material";
import FormInputText from "../components/Form/FormInputText";
import FormColorPicker from "../components/Form/FormColorPicker";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FormSwitch from "../components/Form/FormSwitch";
import { CHART_WIDTH_OPTIONS } from "../models/company.model";
import FormInputNumber from "../components/Form/FormInputNumber";
import { getTableColumnLabelSuffix } from "../lib/group.table.helper";

const STORE_COLUMN_OPTIONS = [
  { label: "Store Name", value: "name" },
  { label: "Store ID", value: "storeId" },
  { label: "Store Area", value: "area" },
  { label: "Store Region", value: "region" },
] as const;

const STORE_COLUMN_OPTIONS_ARRAY = STORE_COLUMN_OPTIONS.map((opt) => ({
  label: opt.label,
  value: opt.value,
}));

const DoughnutEditForm = ({ control, setError }: { control: any; setError: any }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "data",
    rules: {
      required: "Please add chart data",
    },
  });

  return (
    <>
      <Grid item xs={12}>
        <b>Center Text</b>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormInputText name="doughnut.center.prefix" control={control} setError={setError} label="Prefix" />
      </Grid>
      <Grid item xs={12} md={4}>
        <FormInputText name="doughnut.center.suffix" control={control} setError={setError} label="Suffix" />
      </Grid>
      <Grid item xs={12} md={4}>
        <FormSelection
          name="doughnut.center.decimalPlaces"
          control={control}
          setError={setError}
          label="Decimal places"
          defaultValue={0}
          onValueParse={(value) => value === '' ? 0 : Number.parseInt(value)}
          noEmpty
          options={CHART_DECIMAL_PLACES_OPTIONS.map((decimalPlaces) => ({
            label: `${decimalPlaces}`,
            value: decimalPlaces,
          }))}
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputText
          name="doughnut.center.expression"
          control={control}
          setError={setError}
          required
          label="Calculation Expression"
          rules={{
            required: "Expression is required",
            pattern: {
              value: /^(\(*\w+|\d+\)*)(\s[+\-*/]\s\(*(\w+|\d+)\)*)*$/,
              message: "Invalid expression",
            },
          }}
          helperText={
            "Enter a mathematical expression using key. Operators (+, -, *, /) must have spaces before and after them. e.g. osat_1_key_1 + osat_1_key_2"
          }
          multiline
          rows={3}
        />
      </Grid>
      <Grid item xs={12}>
        <b>Chart Data*</b>
      </Grid>
      {fields.map((data, i) => {
        return (
          <Grid item xs={12} key={`${JSON.stringify(data)}${i}`}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={3}>
                <Grid container item xs={11} spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormInputText
                      name={`data.${i}.label`}
                      control={control}
                      setError={setError}
                      required
                      label="Title"
                      rules={{ required: "Title is required" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormColorPicker
                      name={`data.${i}.color`}
                      control={control}
                      setError={setError}
                      label={"Background Color"}
                      required
                      rules={{ required: "Background color is required" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormSelection
                      name={`data.${i}.decimalPlaces`}
                      control={control}
                      setError={setError}
                      label="Decimal places"
                      defaultValue={0}
                      onValueParse={(value) => value === '' ? 0 : Number.parseInt(value)}
                      noEmpty
                      options={CHART_DECIMAL_PLACES_OPTIONS.map((decimalPlaces) => ({
                        label: `${decimalPlaces}`,
                        value: decimalPlaces,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputText
                      name={`data.${i}.expression`}
                      control={control}
                      setError={setError}
                      required
                      label="Calculation Expression"
                      rules={{
                        required: "Expression is required",
                        pattern: {
                          value: /^(\(*\w+|\d+\)*)(\s[+\-*/]\s\(*(\w+|\d+)\)*)*$/,
                          message: "Invalid expression",
                        },
                      }}
                      multiline
                      helperText={
                        "Enter a mathematical expression using key. Operators (+, -, *, /) must have spaces before and after them. e.g. osat_1_key_1 + osat_1_key_2"
                      }
                      rows={3}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={1}>
                  <Stack justifyContent={"center"}>
                    <Box>
                      <IconButton onClick={() => remove(i)}>
                        <ClearIcon color="error" />
                      </IconButton>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <Button variant="contained" color="primary" onClick={() => append({ label: "", expression: "", color: "" })}>
          Add Data
        </Button>
      </Grid>
    </>
  );
};

const BarEditForm = ({
  control,
  setError,
  watch,
  setValue,
}: {
  control: any;
  setError: any;
  watch: any;
  setValue: any;
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "data",
    rules: {
      required: "Please add chart data",
    },
  });
  const watchGroupBy = watch("groupBy");

  useEffect(() => {
    if (watchGroupBy) {
      setValue("bar.dataSource", ChartDataSourceEnum.DateRange);
    }
  }, [watchGroupBy, setValue]);

  return (
    <>
      <Grid item xs={12}>
        <b>Properties</b>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormSelection
          name="bar.direction"
          control={control}
          setError={setError}
          label="Direction"
          options={enumToArraySelection(BarDirectionEnum)}
          noEmpty
          required
          rules={{ required: "Direction is required" }}
        />
      </Grid>
      {!watchGroupBy && (
        <Grid item xs={12} md={6}>
          <FormSelection
            name="bar.dataSource"
            control={control}
            setError={setError}
            label="Data Source"
            options={enumToArraySelection(ChartDataSourceEnum)}
            noEmpty
            required
            rules={{ required: "Data source is required" }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <FormSelection
          name="bar.tickesFormat"
          control={control}
          setError={setError}
          label="Tickes Format"
          options={enumToArraySelection(ChartTickesFormatEnum)}
        />
      </Grid>
      <Grid item xs={12}>
        <b>Chart Data*</b>
      </Grid>
      {fields.map((data, i) => {
        return (
          <Grid item xs={12} key={`${JSON.stringify(data)}${i}`}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={3}>
                <Grid container item xs={11} spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormInputText
                      name={`data.${i}.label`}
                      control={control}
                      setError={setError}
                      required
                      label="Title"
                      rules={{ required: "Title is required" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormColorPicker
                      name={`data.${i}.color`}
                      control={control}
                      setError={setError}
                      label={"Background Color"}
                      required
                      rules={{ required: "Background color is required" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormSelection
                      name={`data.${i}.decimalPlaces`}
                      control={control}
                      setError={setError}
                      label="Decimal places"
                      defaultValue={0}
                      onValueParse={(value) => value === '' ? 0 : Number.parseInt(value)}
                      noEmpty
                      options={CHART_DECIMAL_PLACES_OPTIONS.map((decimalPlaces) => ({
                        label: `${decimalPlaces}`,
                        value: decimalPlaces,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputText
                      name={`data.${i}.expression`}
                      control={control}
                      setError={setError}
                      required
                      label="Calculation Expression"
                      rules={{
                        required: "Expression is required",
                        pattern: {
                          value: /^(\(*\w+|\d+\)*)(\s[+\-*/]\s\(*(\w+|\d+)\)*)*$/,
                          message: "Invalid expression",
                        },
                      }}
                      multiline
                      helperText={
                        "Enter a mathematical expression using key. Operators (+, -, *, /) must have spaces before and after them. e.g. osat_1_key_1 + osat_1_key_2"
                      }
                      rows={3}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={1}>
                  <Stack justifyContent={"center"}>
                    <Box>
                      <IconButton onClick={() => remove(i)}>
                        <ClearIcon color="error" />
                      </IconButton>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <Button variant="contained" color="primary" onClick={() => append({ label: "", expression: "", color: "" })}>
          Add Data
        </Button>
      </Grid>
    </>
  );
};

const LineEditForm = ({
  control,
  setError,
  watch,
  setValue,
}: {
  control: any;
  setError: any;
  watch: any;
  setValue: any;
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "data",
    rules: {
      required: "Please add chart data",
    },
  });
  const watchGroupBy = watch("groupBy");

  useEffect(() => {
    if (watchGroupBy) {
      setValue("line.dataSource", ChartDataSourceEnum.DateRange);
    }
  }, [watchGroupBy, setValue]);

  return (
    <>
      <Grid item xs={12}>
        <b>Properties</b>
      </Grid>
      {!watchGroupBy && (
        <Grid item xs={12} md={6}>
          <FormSelection
            name="line.dataSource"
            control={control}
            setError={setError}
            label="Data Source"
            options={enumToArraySelection(ChartDataSourceEnum)}
            noEmpty
            required
            rules={{ required: "Data source is required" }}
          />
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <FormSelection
          name="line.tickesFormat"
          control={control}
          setError={setError}
          label="Tickes Format"
          options={enumToArraySelection(ChartTickesFormatEnum)}
        />
      </Grid>
      <Grid item xs={12}>
        <b>Chart Data*</b>
      </Grid>
      {fields.map((data, i) => {
        return (
          <Grid item xs={12} key={`${JSON.stringify(data)}${i}`}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={3}>
                <Grid container item xs={11} spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormInputText
                      name={`data.${i}.label`}
                      control={control}
                      setError={setError}
                      required
                      label="Title"
                      rules={{ required: "Title is required" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormColorPicker
                      name={`data.${i}.color`}
                      control={control}
                      setError={setError}
                      label={"Background Color"}
                      required
                      rules={{ required: "Background color is required" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormSelection
                      name={`data.${i}.decimalPlaces`}
                      control={control}
                      setError={setError}
                      label="Decimal places"
                      defaultValue={0}
                      onValueParse={(value) => value === '' ? 0 : Number.parseInt(value)}
                      noEmpty
                      options={CHART_DECIMAL_PLACES_OPTIONS.map((decimalPlaces) => ({
                        label: `${decimalPlaces}`,
                        value: decimalPlaces,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputText
                      name={`data.${i}.expression`}
                      control={control}
                      setError={setError}
                      required
                      label="Calculation Expression"
                      rules={{
                        required: "Expression is required",
                        pattern: {
                          value: /^(\(*\w+|\d+\)*)(\s[+\-*/]\s\(*(\w+|\d+)\)*)*$/,
                          message: "Invalid expression",
                        },
                      }}
                      multiline
                      helperText={
                        "Enter a mathematical expression using key. Operators (+, -, *, /) must have spaces before and after them. e.g. osat_1_key_1 + osat_1_key_2"
                      }
                      rows={3}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={1}>
                  <Stack justifyContent={"center"}>
                    <Box>
                      <IconButton onClick={() => remove(i)}>
                        <ClearIcon color="error" />
                      </IconButton>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <Button variant="contained" color="primary" onClick={() => append({ label: "", expression: "", color: "" })}>
          Add Data
        </Button>
      </Grid>
    </>
  );
};

const MixedEditForm = ({ control, setError }: { control: any; setError: any }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "data",
    rules: {
      required: "Please add chart data",
    },
  });

  return (
    <>
      <Grid item xs={12}>
        <b>Properties</b>
      </Grid>
      <Grid item xs={12}>
        <FormSelection
          name="mixed.tickesFormat"
          control={control}
          setError={setError}
          label="Tickes Format"
          options={enumToArraySelection(ChartTickesFormatEnum)}
        />
      </Grid>
      <Grid item xs={12}>
        <b>Chart Data*</b>
      </Grid>
      {fields.map((data, i) => {
        return (
          <Grid item xs={12} key={`${JSON.stringify(data)}${i}`}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={3}>
                <Grid container item xs={11} spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormInputText
                      name={`data.${i}.label`}
                      control={control}
                      setError={setError}
                      required
                      label="Title"
                      rules={{ required: "Title is required" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormColorPicker
                      name={`data.${i}.color`}
                      control={control}
                      setError={setError}
                      label={"Background Color"}
                      required
                      rules={{ required: "Background color is required" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormSelection
                      name={`data.${i}.decimalPlaces`}
                      control={control}
                      setError={setError}
                      label="Decimal places"
                      defaultValue={0}
                      noEmpty
                      onValueParse={(value) => value === '' ? 0 : Number.parseInt(value)}
                      options={CHART_DECIMAL_PLACES_OPTIONS.map((decimalPlaces) => ({
                        label: `${decimalPlaces}`,
                        value: decimalPlaces,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSelection
                      name={`data.${i}.mixedType`}
                      control={control}
                      setError={setError}
                      label="Display Type"
                      options={[
                        { label: "Line", value: ChartTypeEnum.Line },
                        { label: "Bar", value: ChartTypeEnum.Bar },
                      ]}
                      noEmpty
                      required
                      rules={{ required: "Display type is required" }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputText
                      name={`data.${i}.expression`}
                      control={control}
                      setError={setError}
                      required
                      label="Calculation Expression"
                      rules={{
                        required: "Expression is required",
                        pattern: {
                          value: /^(\(*\w+|\d+\)*)(\s[+\-*/]\s\(*(\w+|\d+)\)*)*$/,
                          message: "Invalid expression",
                        },
                      }}
                      multiline
                      helperText={
                        "Enter a mathematical expression using key. Operators (+, -, *, /) must have spaces before and after them. e.g. osat_1_key_1 + osat_1_key_2"
                      }
                      rows={3}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={1}>
                  <Stack justifyContent={"center"}>
                    <Box>
                      <IconButton onClick={() => remove(i)}>
                        <ClearIcon color="error" />
                      </IconButton>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <Button variant="contained" color="primary" onClick={() => append({ label: "", expression: "", color: "" })}>
          Add Data
        </Button>
      </Grid>
    </>
  );
};

const TableColumnLabelField = ({
  index,
  control,
  setError,
  groupBy,
}: {
  index: number;
  control: any;
  setError: any;
  groupBy: boolean;
}) => {
  const tableDateRange = useWatch({ control, name: `data.${index}.tableDateRange` });
  const tableDateRangeValue = useWatch({ control, name: `data.${index}.tableDateRangeValue` });
  const suffix = groupBy ? getTableColumnLabelSuffix(tableDateRange, tableDateRangeValue) : null;

  return (
    <FormInputText
      name={`data.${index}.label`}
      control={control}
      setError={setError}
      required
      label="Column Label"
      rules={{ required: "Label is required" }}
      InputProps={{
        endAdornment: suffix ? (
          <InputAdornment position="end">
            <Typography variant="body2" color="text.secondary" noWrap component="span">
              {suffix}
            </Typography>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
};

const TableDateRangeField = ({
  index,
  control,
  setError,
}: {
  index: number;
  control: any;
  setError: any;
}) => {
  const tableDateRange = useWatch({ control, name: `data.${index}.tableDateRange` });
  const isAll = tableDateRange === TableDateRangeEnum.All;

  return (
    <Box sx={{ position: "relative" }}>
      <FormInputNumber
        name={`data.${index}.tableDateRangeValue`}
        control={control}
        setError={setError}
        fullWidth
        label="Date Range Value"
        defaultValue={0}
        type="int"
        disabled={isAll}
        required={!isAll}
        rules={{
          validate: (value) => {
            if (isAll) return true;
            if (value === "" || value === undefined || value === null) {
              return "Date Range Value is required";
            }
            if (typeof value === "number" && value < 0) {
              return "Date Range Value must be greater than or equal to 0";
            }
            return true;
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 8,
          top: "12.5px",
          background: "transparent",
          width: 100,
        }}
      >
        <FormSelection
          size="small"
          name={`data.${index}.tableDateRange`}
          control={control}
          setError={setError}
          required
          noEmpty
          label="Date Range"
          defaultValue={TableDateRangeEnum.All}
          options={enumToArraySelection(TableDateRangeEnum)}
          sx={{
            "& .MuiInputLabel-root": { display: "none" },
            "& .MuiOutlinedInput-root": {
              backgroundColor: "action.hover",
              borderRadius: 999,
              transition: "background-color 0.15s ease-in-out",
              "&:hover": { backgroundColor: "action.selected" },
              "&.Mui-focused": { backgroundColor: "action.selected" },
            },
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "& .MuiSelect-select": {
              py: 0.5,
              pl: 1.5,
              pr: "28px !important",
              fontSize: "0.8125rem",
              fontWeight: 500,
              color: "text.primary",
            },
            "& .MuiSelect-icon": {
              right: 6,
              fontSize: "1.125rem",
              color: "text.secondary",
            },
            "& .MuiFormHelperText-root": { display: "none" },
          }}
        />
      </Box>
    </Box>
  );
};

const TableEditForm = ({ control, setError, watch }: { control: any; setError: any; watch: any }) => {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "data",
    rules: {
      required: "Please add chart data",
    },
  });
  const groupBy = watch("groupBy");

  return (
    <>
      <Grid item xs={12}>
        <b>Chart Data*</b>
      </Grid>
      {!groupBy && (
        <Grid item xs={12}>
          <Typography variant="body1">
            For column key please use textbox question key since it can only show texts
          </Typography>
        </Grid>
      )}
      {fields.map((data, i) => {
        return (
          <Grid item xs={12} key={data.id}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={3}>
                <Grid container item xs={11} spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TableColumnLabelField
                      index={i}
                      control={control}
                      setError={setError}
                      groupBy={!!groupBy}
                    />
                  </Grid>
                  {groupBy && (
                    <Grid item xs={12} md={6}>
                      <TableDateRangeField index={i} control={control} setError={setError} />
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <FormInputText
                      name={`data.${i}.expression`}
                      control={control}
                      setError={setError}
                      required
                      label="Column Key"
                      rules={{
                        required: "Key is required",
                        pattern: {
                          value: /^(\(*\w+|\d+\)*)(\s[+\-*/]\s\(*(\w+|\d+)\)*)*$/,
                          message: "Invalid key",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={groupBy ? 3 : 6}>
                    <FormSelection
                      name={`data.${i}.decimalPlaces`}
                      control={control}
                      setError={setError}
                      label="Decimal places"
                      defaultValue={0}
                      noEmpty
                      onValueParse={(value) => value === '' ? 0 : Number.parseInt(value)}
                      options={CHART_DECIMAL_PLACES_OPTIONS.map((decimalPlaces) => ({
                        label: `${decimalPlaces}`,
                        value: decimalPlaces,
                      }))}
                    />
                  </Grid>
                  {groupBy && (
                    <Grid item xs={12} md={3}>
                      <FormSelection
                        name={`data.${i}.tableDataFormation`}
                        control={control}
                        setError={setError}
                        noEmpty
                        label="Data Formation"
                        options={enumToArraySelection(TableDataFormationEnum)}
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={1}>
                  <Stack justifyContent={"center"}>
                    <Box>
                      <IconButton onClick={() => remove(i)} size="small">
                        <ClearIcon color="error" />
                      </IconButton>
                    </Box>
                    <Box sx={{ visibility: i < fields.length - 1 ? "visible" : "hidden" }}>
                      <IconButton onClick={() => move(i, i + 1)} size="small">
                        <ArrowDownwardIcon color="primary" />
                      </IconButton>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            append({
              label: "",
              expression: "",
              color: "",
              tableDateRange: TableDateRangeEnum.All,
              tableDataFormation: TableDataFormationEnum.Default,
            })
          }
        >
          Add Data
        </Button>
      </Grid>
    </>
  );
};

const ChartEditForm = forwardRef((props: { defaultValues: Chart }, ref) => {
  const methods = useForm<Chart>({
    defaultValues: {
      ...props.defaultValues,
      storeLabelTypes: props.defaultValues?.storeLabelTypes || [],
    },
  });
  const { handleSubmit, control, setError, watch, getValues, reset, setValue } = methods;
  const watchType = watch("type");
  watch("data");
  useImperativeHandle(ref, () => ({
    onSubmit: () => {
      return onHandleFormSubmit(handleSubmit, async (data) => {
        if (data.groupBy !== ChartGroupByEnum.Store) {
          data.storeLabelTypes = [];
        }
        return data;
      });
    },
  }));

  useEffect(() => {
    if (watchType !== props.defaultValues.type) {
      let { data, label, type, width, storeLabelTypes } = getValues();
      reset({
        data,
        label,
        type,
        width,
        storeLabelTypes: storeLabelTypes || [],
      });
    }
  }, [watchType, getValues, reset, props.defaultValues.type]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <FormSelection
          name="type"
          control={control}
          setError={setError}
          label="Chart Type"
          options={enumToArraySelection(ChartTypeEnum)}
          noEmpty
          required
          rules={{ required: "Chart Type is required" }}
        />
      </Grid>
      <Grid item xs={6}>
        <FormSelection
          name="width"
          control={control}
          setError={setError}
          label="Chart Width"
          onValueParse={(value: string) => {
            return parseInt(value);
          }}
          options={CHART_WIDTH_OPTIONS.map((width) => ({ label: `${width} Columns`, value: width }))}
          noEmpty
          required
          rules={{ required: "Chart Type is required" }}
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputText
          name="label"
          control={control}
          setError={setError}
          required
          label="Chart Title"
          rules={{ required: "Chart title is required" }}
          helperText="e.g. Vape Proportion Percent"
        />
      </Grid>
      <Grid item xs={12}>
        <FormSelection
          name="groupBy"
          control={control}
          setError={setError}
          label="Group By"
          options={enumToArraySelection(ChartGroupByEnum)}
        />
      </Grid>
      {watch("groupBy") && (
        <>
          <Grid item xs={4}>
            <FormSwitch name="showInHierarchy" control={control} setError={setError} label="Show in Hierarchy" />
          </Grid>
          {watch("groupBy") === ChartGroupByEnum.Store && (
            <Grid item xs={8}>
              <FormSelection
                name="storeLabelTypes"
                control={control}
                setError={setError}
                label="Store Columns"
                options={STORE_COLUMN_OPTIONS_ARRAY}
                multiple={true}
              />
            </Grid>
          )}
        </>
      )}

      {watchType === ChartTypeEnum.Doughnut && <DoughnutEditForm control={control} setError={setError} />}
      {watchType === ChartTypeEnum.Bar && (
        <BarEditForm control={control} setError={setError} watch={watch} setValue={setValue} />
      )}
      {watchType === ChartTypeEnum.Line && (
        <LineEditForm control={control} setError={setError} watch={watch} setValue={setValue} />
      )}
      {watchType === ChartTypeEnum.Mixed && <MixedEditForm control={control} setError={setError} />}
      {watchType === ChartTypeEnum.Table && <TableEditForm control={control} setError={setError} watch={watch} />}
    </Grid>
  );
});

export default ChartEditForm;
